import { Badge } from 'src/design/atoms/Badge';
import { ColorSwatch } from 'src/design/atoms/ColorSwatch';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { PayCodeDto } from 'src/api/FiretrolApiClient';
import { useDialogs } from 'src/hooks/useDialogs';
import { useOrganization } from 'src/hooks/useOrganization';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

const getColumns = (): MRT_ColumnDef<PayCodeDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'rateModifierMultiplier',
      header: 'Rate Modifier Multiplier',
      Cell: ({ cell }) => {
        const value = cell.getValue<number>();
        return value?.toFixed(2) || '0.00';
      },
    },
    {
      accessorKey: 'bypassScalePay',
      header: 'Bypass Scale Pay',
      Cell: ({ cell }) => {
        const value = cell.getValue<boolean>();
        return value ? (
          <Badge color="orange" variant="light">
            Bypassed
          </Badge>
        ) : null;
      },
    },
    {
      accessorKey: 'isDirect',
      header: 'Type',
      Cell: ({ cell }) => {
        const isDirect = cell.getValue<boolean>();
        return isDirect ? (
          <Badge color="blue" variant="light">
            Direct
          </Badge>
        ) : (
          <Badge color="grape" variant="light">
            Indirect
          </Badge>
        );
      },
    },
    {
      accessorKey: 'isOvertime',
      header: 'Overtime',
      Cell: ({ cell }) => {
        const value = cell.getValue<boolean>();
        return value ? (
          <Badge color="orange" variant="light">
            Overtime
          </Badge>
        ) : null;
      },
    },
    {
      accessorKey: 'color',
      header: 'Color',
      Cell: ({ cell }) => {
        const value = cell.getValue<string | undefined>();
        return value ? <ColorSwatch color={value} size={20} /> : null;
      },
    },
  ];
};

export const PayCodes: React.FC = () => {
  const { setAddPayCodeOpen, refreshPayCodesCounter } = useDialogs();
  const { payCodes, payCodesLoading, refreshPayCodes } = useOrganization();

  React.useEffect(() => {
    refreshPayCodes();
  }, [refreshPayCodesCounter]);

  const columns = useMemo(() => {
    return getColumns();
  }, []);

  const tableState = useTableState({
    identifier: `pay-codes`,
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 150 } },
      renderRowActions: ({ row }) => {
        const payCode = row.original;
        return (
          <Group>
            <EditActionIcon onClick={() => setAddPayCodeOpen(true, payCode)} />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: payCodes,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => setAddPayCodeOpen(true)}>
            Add Pay Code
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: payCodes.length,
    isLoading: payCodesLoading,
    isFetching: false,
    isError: false,
  });

  return (
    <>
      <Header
        title="Pay Codes"
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Pay Codes' }],
        }}
        helpText="Pay codes define rate modifiers for different types of pay, such as overtime or double time."
      />
      {table}
    </>
  );
};
